/**
 * Spa Booking Page
 */

import { Header, renderServiceTabs } from '../components';
import { setState, navigateTo } from '../store';
import { getSpaConfig } from '../api';
import { getCurrentLanguage } from '../i18n';

interface LocalizedString { th: string; en: string; zh: string; }
interface Treatment {
  code: string;
  name: LocalizedString | string;
  nameTh?: string;
  nameEn?: string;
  nameZh?: string;
  duration: number;
  price: number;
  desc?: string;
}

interface OutletImage {
  url: string;
  thumbUrl?: string;
}

interface SpaOutletData {
  id?: string;
  name?: LocalizedString | string;
  description?: LocalizedString | string;
  enabled?: boolean;
  openTime?: string;
  closeTime?: string;
  slotIntervalMinutes?: number;
  maxBedsPerSlot?: number;
  images?: OutletImage[];
  treatments?: Treatment[];
  outlets?: SpaOutletData[];
}

let spaOutlets: SpaOutletData[] = [];
let activeOutletId: string | null = null;
let spaConfigLoaded = false;

let TREATMENTS: Treatment[] = [
  { code: 'AROMA_60', name: { th: 'นวดอโรม่าเทอราพี (Aromatherapy Massage)', en: 'Aromatherapy Massage (60 min)', zh: '芳疗按摩 (60分钟)' }, duration: 60, price: 1500, desc: 'ผ่อนคลายด้วยน้ำมันหอมระเหยสูตรพิเศษ' },
  { code: 'THAI_90',  name: { th: 'นวดแผนไทยโบราณ (Traditional Thai Massage)', en: 'Traditional Thai Massage (90 min)', zh: '泰式按摩 (90分钟)' }, duration: 90, price: 1800, desc: 'นวดกดจุดผ่อนคลายกล้ามเนื้อทั่วร่างกาย' },
  { code: 'FOOT_60',  name: { th: 'นวดกดจุดฝ่าเท้า (Foot Reflexology)', en: 'Foot Reflexology (60 min)', zh: '足部按摩 (60分钟)' }, duration: 60, price: 1000, desc: 'นวดกดจุดสะท้อนเท้าเพื่อสุขภาพ' },
];

const TIME_SLOTS = ['10:00', '11:30', '13:00', '14:00', '15:30', '17:00', '18:30'];

let selectedTreatmentCode = 'AROMA_60';
let selectedDateStr = new Date().toISOString().split('T')[0];
let selectedTimeSlot = '14:00';
let paxCount = 2;
let roomNumberInput = '';

function getLocalizedText(val: any, lang: string, fallback: string = ''): string {
  if (!val) return fallback;
  if (typeof val === 'string') return val;
  if (typeof val === 'object') {
    return val[lang] || val.th || val.en || val.zh || fallback;
  }
  return fallback;
}

export function renderSpaPage(): string {
  const lang = getCurrentLanguage();
  const activeOutlet = spaOutlets.find(o => o.id === activeOutletId) || spaOutlets[0] || {};
  const currentTreatments = activeOutlet.treatments || TREATMENTS;
  const outletImages = activeOutlet.images || [];

  const dates = [];
  const today = new Date();
  for (let i = 0; i < 7; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    const iso = d.toISOString().split('T')[0];
    const dayLabel = d.toLocaleDateString(lang === 'th' ? 'th-TH' : lang === 'zh' ? 'zh-CN' : 'en-US', { weekday: 'short', day: 'numeric', month: 'short' });
    dates.push({ iso, label: dayLabel });
  }

  const selectedTreat = currentTreatments.find(t => t.code === selectedTreatmentCode) || currentTreatments[0] || TREATMENTS[0];
  const totalPrice = (selectedTreat.price || 1000) * paxCount;
  const spaNameText = getLocalizedText(activeOutlet.name, lang, 'สปา (Spa)');
  const spaDescText = getLocalizedText(activeOutlet.description, lang, '');
  const openHoursText = activeOutlet.openTime && activeOutlet.closeTime ? `${activeOutlet.openTime} – ${activeOutlet.closeTime}` : '09:00 – 21:00';

  const uiText = {
    th: {
      dateTitle: '📅 เลือกรอบวันและเวลา',
      treatTitle: '💆 เลือกทรีตเมนต์สปา',
      paxTitle: '👥 จำนวนผู้รับบริการ (Guests)',
      paxSub: 'จำนวนผู้รับบริการนวด',
      roomLabel: '🏨 หมายเลขห้องพัก (กรณีเป็นแขกพักในโรงแรม - Optional)',
      roomPlace: 'ระบุหมายเลขห้อง เช่น 1002',
      totalPriceLabel: 'ราคารวม (ชำระเงินที่ร้าน)',
      nextBtn: 'ถัดไป: กรอกข้อมูลผู้ใช้บริการ ›',
      openHours: '🕒 เวลาทำการ',
      mins: 'นาที',
    },
    en: {
      dateTitle: '📅 Select Date & Time',
      treatTitle: '💆 Select Spa Treatment',
      paxTitle: '👥 Number of Guests',
      paxSub: 'Number of guests for massage',
      roomLabel: '🏨 Room Number (For In-house Guests - Optional)',
      roomPlace: 'e.g. Room 1002',
      totalPriceLabel: 'Total Price (Pay at Spa)',
      nextBtn: 'Next: Guest Details ›',
      openHours: '🕒 Operating Hours',
      mins: 'mins',
    },
    zh: {
      dateTitle: '📅 选择日期与时间',
      treatTitle: '💆 选择水疗项目',
      paxTitle: '👥 客人人数',
      paxSub: '按摩服务人数',
      roomLabel: '🏨 房间号 (店内住客 - 可选)',
      roomPlace: '例如: 1002',
      totalPriceLabel: '总价 (到店支付)',
      nextBtn: '下一步：填写客人信息 ›',
      openHours: '🕒 营业时间',
      mins: '分钟',
    },
  }[lang] || {
    dateTitle: '📅 Select Date & Time',
    treatTitle: '💆 Select Spa Treatment',
    paxTitle: '👥 Number of Guests',
    paxSub: 'Number of guests for massage',
    roomLabel: '🏨 Room Number (For In-house Guests - Optional)',
    roomPlace: 'e.g. Room 1002',
    totalPriceLabel: 'Total Price (Pay at Spa)',
    nextBtn: 'Next: Guest Details ›',
    openHours: '🕒 Operating Hours',
    mins: 'mins',
  };

  return `
    ${Header(spaNameText, false, true)}
    ${renderServiceTabs('spa')}

    <div class="page-container" style="padding:16px;padding-bottom:100px;">

      <!-- Multi-Outlet Selector Tabs (if > 1 outlet) -->
      ${spaOutlets.length > 1 ? `
        <div style="display:flex;gap:8px;overflow-x:auto;padding-bottom:8px;margin-bottom:12px;">
          ${spaOutlets.map(o => `
            <button
              onclick="window.selectSpaOutlet?.('${o.id}')"
              style="padding:8px 14px;border-radius:20px;border:1px solid ${activeOutletId === o.id ? '#00B900' : '#e0e0e0'};background:${activeOutletId === o.id ? '#00B900' : '#fff'};color:${activeOutletId === o.id ? '#fff' : '#333'};font-size:13px;font-weight:600;white-space:nowrap;cursor:pointer;"
            >
              🌿 ${getLocalizedText(o.name, lang, o.id || 'Spa')}
            </button>
          `).join('')}
        </div>
      ` : ''}

      <!-- Cover Image Banner & Gallery Slider -->
      ${outletImages.length > 0 ? `
        <div style="margin-bottom:16px;border-radius:12px;overflow:hidden;box-shadow:0 2px 6px rgba(0,0,0,0.1);background:#fff;">
          <img src="${outletImages[0].url || outletImages[0].thumbUrl}" alt="Spa Cover" style="width:100%;height:180px;object-fit:cover;display:block;" />
          ${outletImages.length > 1 ? `
            <div style="display:flex;gap:6px;padding:8px;overflow-x:auto;background:#fafafa;border-top:1px solid #f0f0f0;">
              ${outletImages.map(img => `
                <img src="${img.thumbUrl || img.url}" alt="" style="width:50px;height:50px;object-fit:cover;border-radius:6px;border:1px solid #e0e0e0;flex-shrink:0;" />
              `).join('')}
            </div>
          ` : ''}
        </div>
      ` : ''}

      <!-- Outlet Information Card -->
      <div class="card" style="background:#fff;border-radius:12px;padding:16px;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.08);">
        <h2 style="font-size:18px;font-weight:700;color:#1a1a1a;margin-bottom:4px;">${spaNameText}</h2>
        ${spaDescText ? `<p style="font-size:13px;color:#666;line-height:1.4;margin-bottom:10px;">${spaDescText}</p>` : ''}
        <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
          <span style="display:inline-flex;align-items:center;padding:4px 10px;background:#E8F5E8;color:#00B900;border-radius:12px;font-size:12px;font-weight:600;">
            ${uiText.openHours}: ${openHoursText}
          </span>
          ${activeOutlet.slotIntervalMinutes ? `
            <span style="display:inline-flex;align-items:center;padding:4px 10px;background:#f5f5f5;color:#666;border-radius:12px;font-size:12px;">
              ⏱️ ${activeOutlet.slotIntervalMinutes} ${uiText.mins}/slot
            </span>
          ` : ''}
        </div>
      </div>
      
      <!-- Date & Time Picker Card -->
      <div class="card" style="background:#fff;border-radius:12px;padding:16px;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.08);">
        <h3 style="font-size:16px;font-weight:600;margin-bottom:12px;color:#1a1a1a;">${uiText.dateTitle}</h3>
        
        <!-- Date Chips -->
        <div style="display:flex;gap:8px;overflow-x:auto;padding-bottom:8px;margin-bottom:12px;">
          ${dates.map(d => `
            <button 
              class="date-chip ${selectedDateStr === d.iso ? 'active' : ''}" 
              onclick="window.selectSpaDate?.('${d.iso}')"
              style="padding:8px 12px;border-radius:8px;border:1px solid ${selectedDateStr === d.iso ? '#00B900' : '#e0e0e0'};background:${selectedDateStr === d.iso ? '#E8F5E8' : '#fff'};color:${selectedDateStr === d.iso ? '#00B900' : '#333'};font-size:13px;font-weight:${selectedDateStr === d.iso ? '600' : '400'};white-space:nowrap;cursor:pointer;"
            >
              ${d.label}
            </button>
          `).join('')}
        </div>

        <!-- Time Slot Chips -->
        <div style="display:flex;flex-wrap:wrap;gap:8px;">
          ${TIME_SLOTS.map(slot => `
            <button 
              class="time-chip ${selectedTimeSlot === slot ? 'active' : ''}"
              onclick="window.selectSpaTimeSlot?.('${slot}')"
              style="padding:8px 14px;border-radius:20px;border:1px solid ${selectedTimeSlot === slot ? '#00B900' : '#e0e0e0'};background:${selectedTimeSlot === slot ? '#00B900' : '#fff'};color:${selectedTimeSlot === slot ? '#fff' : '#333'};font-size:13px;font-weight:600;cursor:pointer;"
            >
              ${slot}
            </button>
          `).join('')}
        </div>
      </div>

      <!-- Treatment Selector Card -->
      <div class="card" style="background:#fff;border-radius:12px;padding:16px;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.08);">
        <h3 style="font-size:16px;font-weight:600;margin-bottom:12px;color:#1a1a1a;">${uiText.treatTitle}</h3>
        <div style="display:flex;flex-direction:column;gap:12px;">
          ${currentTreatments.map(t => {
            const treatName = getLocalizedText(t.name, lang, t.nameTh || t.code);
            return `
              <div 
                onclick="window.selectSpaTreatment?.('${t.code}')"
                style="display:flex;align-items:center;gap:12px;padding:12px;border-radius:10px;border:1.5px solid ${selectedTreatmentCode === t.code ? '#00B900' : '#e0e0e0'};background:${selectedTreatmentCode === t.code ? '#F9FDF9' : '#fff'};cursor:pointer;"
              >
                <input type="radio" name="spa_treatment" ${selectedTreatmentCode === t.code ? 'checked' : ''} style="accent-color:#00B900;width:18px;height:18px;">
                <div style="flex:1;">
                  <div style="font-weight:600;font-size:14px;color:#1a1a1a;">${treatName}</div>
                  <div style="font-size:12px;color:#666;margin-top:2px;">⏱️ ${t.duration} ${uiText.mins} ${t.desc ? '• ' + t.desc : ''}</div>
                </div>
                <div style="font-weight:700;font-size:15px;color:#00B900;">฿${(t.price || 0).toLocaleString()}</div>
              </div>
            `;
          }).join('')}
        </div>
      </div>

      <!-- Guest Count & Room Number Card -->
      <div class="card" style="background:#fff;border-radius:12px;padding:16px;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.08);">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:16px;">
          <div>
            <div style="font-size:15px;font-weight:600;color:#1a1a1a;">${uiText.paxTitle}</div>
            <div style="font-size:12px;color:#666;">${uiText.paxSub}</div>
          </div>
          <div style="display:flex;align-items:center;gap:12px;border:1px solid #e0e0e0;border-radius:8px;padding:4px 8px;">
            <button onclick="window.changeSpaPax?.(-1)" style="border:none;background:none;font-size:18px;font-weight:700;color:#00B900;width:28px;cursor:pointer;">-</button>
            <span style="font-size:15px;font-weight:600;min-width:20px;text-align:center;">${paxCount}</span>
            <button onclick="window.changeSpaPax?.(1)" style="border:none;background:none;font-size:18px;font-weight:700;color:#00B900;width:28px;cursor:pointer;">+</button>
          </div>
        </div>

        <div style="border-top:1px solid #f0f0f0;padding-top:12px;">
          <label style="display:block;font-size:13px;font-weight:600;color:#333;margin-bottom:6px;">${uiText.roomLabel}</label>
          <input 
            type="text" 
            id="spa_room_number" 
            value="${roomNumberInput}"
            onchange="window.updateSpaRoomNumber?.(this.value)"
            placeholder="${uiText.roomPlace}"
            style="width:100%;padding:10px 12px;border:1px solid #e0e0e0;border-radius:8px;font-size:14px;outline:none;"
          />
        </div>
      </div>

    </div>

    <!-- Fixed Bottom Action Bar -->
    <div style="position:fixed;bottom:0;left:0;right:0;max-width:480px;margin:0 auto;background:#fff;padding:12px 16px;border-top:1px solid #e0e0e0;box-shadow:0 -2px 10px rgba(0,0,0,0.05);z-index:100;display:flex;align-items:center;justify-content:space-between;">
      <div>
        <div style="font-size:12px;color:#666;">${uiText.totalPriceLabel}</div>
        <div style="font-size:18px;font-weight:700;color:#00B900;">฿${totalPrice.toLocaleString()}</div>
      </div>
      <button 
        onclick="window.proceedSpaToGuestInfo?.()"
        style="padding:12px 20px;background:#00B900;color:#fff;border:none;border-radius:8px;font-size:15px;font-weight:600;cursor:pointer;display:flex;align-items:center;gap:6px;"
      >
        ${uiText.nextBtn}
      </button>
    </div>
  `;
}

export function initSpaPage(): void {
  if (!spaConfigLoaded) {
    spaConfigLoaded = true;
    getSpaConfig().then((cfg: any) => {
      if (cfg) {
        if (cfg.outlets && cfg.outlets.length > 0) {
          spaOutlets = cfg.outlets;
          activeOutletId = spaOutlets[0].id || null;
        } else if (cfg.id) {
          spaOutlets = [cfg];
          activeOutletId = cfg.id || 'spa_main';
        } else {
          spaOutlets = [];
          activeOutletId = null;
        }
        const active = spaOutlets.find(o => o.id === activeOutletId) || spaOutlets[0];
        if (active && active.treatments && active.treatments.length > 0) {
          TREATMENTS = active.treatments;
          if (!TREATMENTS.some(t => t.code === selectedTreatmentCode)) {
            selectedTreatmentCode = TREATMENTS[0].code;
          }
        }
        (window as any).renderApp?.();
      }
    }).catch(err => console.error('[SpaPage] Error fetching dynamic config:', err));
  }

  (window as any).selectSpaOutlet = (outletId: string) => {
    activeOutletId = outletId;
    const active = spaOutlets.find(o => o.id === outletId);
    if (active && active.treatments && active.treatments.length > 0) {
      TREATMENTS = active.treatments;
      if (!TREATMENTS.some(t => t.code === selectedTreatmentCode)) {
        selectedTreatmentCode = TREATMENTS[0].code;
      }
    }
    (window as any).renderApp?.();
  };

  (window as any).selectSpaDate = (iso: string) => {
    selectedDateStr = iso;
    (window as any).renderApp?.();
  };

  (window as any).selectSpaTimeSlot = (slot: string) => {
    selectedTimeSlot = slot;
    (window as any).renderApp?.();
  };

  (window as any).selectSpaTreatment = (code: string) => {
    selectedTreatmentCode = code;
    (window as any).renderApp?.();
  };

  (window as any).changeSpaPax = (delta: number) => {
    paxCount = Math.max(1, Math.min(10, paxCount + delta));
    (window as any).renderApp?.();
  };

  (window as any).updateSpaRoomNumber = (val: string) => {
    roomNumberInput = val;
  };

  (window as any).proceedSpaToGuestInfo = () => {
    const lang = getCurrentLanguage();
    const inputEl = document.getElementById('spa_room_number') as HTMLInputElement;
    if (inputEl) roomNumberInput = inputEl.value;

    const activeOutlet = spaOutlets.find(o => o.id === activeOutletId) || spaOutlets[0] || {};
    const currentTreatments = activeOutlet.treatments || TREATMENTS;
    const treat = currentTreatments.find(t => t.code === selectedTreatmentCode) || currentTreatments[0] || TREATMENTS[0];
    const totalPrice = (treat.price || 1000) * paxCount;
    const treatNameStr = getLocalizedText(treat.name, lang, treat.nameTh || treat.code);

    setState({
      bookingType: 'SPA',
      spaDetails: {
        treatmentCode: treat.code,
        treatmentName: { th: treatNameStr, en: treatNameStr },
        bookingDate: selectedDateStr,
        timeSlot: selectedTimeSlot,
        durationMinutes: treat.duration,
        pax: paxCount,
        roomNumber: roomNumberInput,
        inHouseGuestName: roomNumberInput ? `ห้อง ${roomNumberInput}` : undefined,
      },
      selectedRoom: {
        roomTypeCode: treat.code,
        roomTypeName: `🌿 ${treatNameStr}`,
        ratePlanCode: 'PAY_AT_VENUE',
        ratePlanName: 'ชำระเงินที่ร้าน (Pay at Spa)',
        totalRate: totalPrice,
        ratePerRoom: totalPrice,
        averageNightlyRate: totalPrice,
        totalBeforeTax: totalPrice,
        numberOfRooms: 1,
        currency: 'THB',
        available: 1,
        description: `รอบเวลา ${selectedTimeSlot} น. สำหรับ ${paxCount} ท่าน`,
      },
    });

    navigateTo('guest-info');
  };
}
